// AssinaturaCard.tsx
import React from 'react';
import { AssinaturaClienteView } from './PlanosClienteAssinaturasList';
import { formatPlanPrice, getPlanBillingLabel, getPlanPriceSuffix } from './planosUtils';

interface Props {
    assinatura: AssinaturaClienteView;
    onCancel?: (assinatura: AssinaturaClienteView) => void;
}

function formatDate(date?: string) {
    if (!date) return '—';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR');
}

const AssinaturaCard: React.FC<Props> = ({ assinatura, onCancel }) => {
    const isActive = assinatura.status === 1;
    const { plano, financeiro, periodoAtual } = assinatura;
    const periodServices = periodoAtual?.servicos || [];

    const isSingleLike = assinatura.billingType === 'UNICO' || plano.periodicity === 'single';
    // Com cobranca sem limite, "restantes" e "a pagar" nao tem significado.
    const showRemaining = !isSingleLike && !plano.indeterminateQuantity;

    return (
        <article className="pla-card">
            <div className="pla-sub-head">
                <div className="min-w-0">
                    <h2 className="pla-card-name">{plano.name}</h2>
                    <p className="pla-billing">{getPlanBillingLabel(plano)}</p>
                </div>
                <span className="sf-tag shrink-0" data-tone={isActive ? 'green' : 'gray'}>
                    {isActive ? 'Ativa' : 'Cancelada'}
                </span>
            </div>

            <div>
                <div className="sf-info-row">
                    <span>Valor</span>
                    <span>
                        {formatPlanPrice(plano.price)}
                        {getPlanPriceSuffix(plano.periodicity)}
                    </span>
                </div>
                <div className="sf-info-row">
                    <span>Início</span>
                    <span>{formatDate(assinatura.startAt)}</span>
                </div>
                {isActive && (
                    <div className="sf-info-row">
                        <span>{isSingleLike ? 'Validade' : 'Próxima cobrança'}</span>
                        <span>
                            {isSingleLike
                                ? assinatura.endAt
                                    ? formatDate(assinatura.endAt)
                                    : 'Sem validade definida'
                                : formatDate(assinatura.nextBillingAt)}
                        </span>
                    </div>
                )}
                <div className="sf-info-row">
                    <span>Total pago</span>
                    <span>
                        {formatPlanPrice(financeiro.totalPaidAmount)}
                        {!isSingleLike &&
                            ` · ${financeiro.paidPeriodsCount} ${financeiro.paidPeriodsCount === 1 ? 'período' : 'períodos'}`}
                    </span>
                </div>
                {isActive && showRemaining && (
                    <div className="sf-info-row">
                        <span>A pagar</span>
                        <span>
                            {formatPlanPrice(financeiro.totalRemainingAmount)} · {financeiro.remainingPeriodsCount}{' '}
                            {financeiro.remainingPeriodsCount === 1 ? 'período' : 'períodos'}
                        </span>
                    </div>
                )}
            </div>

            {/* Uso do período atual */}
            {isActive && periodoAtual && (
                <div className="pla-usage">
                    <div className="pla-usage-title">
                        Uso no período
                        <span>
                            {formatDate(periodoAtual.inicio)} a {formatDate(periodoAtual.fim)}
                        </span>
                    </div>

                    {periodServices.length ? (
                        periodServices.map((s) => {
                            const total = s.limit || 0;
                            const used = s.used || 0;
                            const percent = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

                            return (
                                <div key={s.serviceId}>
                                    <div className="pla-usage-item">
                                        <span>{s.serviceName || 'Serviço'}</span>
                                        <span>
                                            {used} de {total}
                                        </span>
                                    </div>
                                    <div
                                        className="pla-usage-bar"
                                        role="progressbar"
                                        aria-valuemin={0}
                                        aria-valuemax={total}
                                        aria-valuenow={used}
                                        aria-label={`Uso de ${s.serviceName || 'serviço'}`}
                                    >
                                        <span style={{ width: `${percent}%` }} />
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <span className="pla-billing">Nenhum serviço registrado neste período.</span>
                    )}
                </div>
            )}

            {isActive && onCancel && (
                <div className="pla-sub-cancel mt-auto">
                    <button type="button" className="sf-destructive-row" onClick={() => onCancel(assinatura)}>
                        Cancelar assinatura
                    </button>
                </div>
            )}
        </article>
    );
};

export default AssinaturaCard;
